import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { DashboardData, InboundPendingSender, InboundStatus } from '@aviaconsole/shared';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { capi } from '../api/client';

type Theme = 'day' | 'night';

export function Shell() {
  const { user, companies, company, setCompany, role, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const [theme, setTheme] = useState<Theme>(() => (document.documentElement.getAttribute('data-theme') as Theme) || 'day');
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomer = role === 'customer';
  // A crm member sees the CRM and nothing of billing: no dashboard, invoices, customers or amounts.
  const isCrm = role === 'crm';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('ac.theme', theme); } catch { /* ignore */ }
  }, [theme]);

  // Close the mobile drawer whenever the route changes, and lock body scroll while it's open.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [navOpen]);

  const counts = useQuery({
    queryKey: ['dashboard', company?.id], enabled: !!company && !isCustomer && !isCrm,
    queryFn: () => capi<DashboardData>('/dashboard'), select: (d) => d.counts,
  });

  // Senders waiting for approval in the invoice mailbox; only asked for where the mailbox is on.
  const mailbox = useQuery({ queryKey: ['inbound-status', company?.id], enabled: !!company && !isCustomer && !isCrm, queryFn: () => capi<InboundStatus>('/inbound') });
  const pending = useQuery({
    queryKey: ['inbound-pending', company?.id], enabled: !!mailbox.data?.enabled, refetchInterval: 120_000,
    queryFn: () => capi<InboundPendingSender[]>('/inbound/pending'),
  });

  const nav = isCrm ? [] : isCustomer ? [{ to: '/portal', label: t('navPortal'), icon: 'fas fa-user-lock' }] : [
    { to: '/', label: t('navDashboard'), icon: 'fas fa-gauge-high' },
    { to: '/invoices', label: t('navInvoices'), icon: 'fas fa-file-invoice', count: counts.data?.invoices },
    { to: '/dossier', label: t('navDossier'), icon: 'fas fa-box-archive' },
    { to: '/mailbox', label: t('navMailbox'), icon: 'fas fa-inbox', count: pending.data?.length },
    { to: '/recurring', label: t('navRecurring'), icon: 'fas fa-repeat', count: counts.data?.recurring },
    { to: '/customers', label: t('navCustomers'), icon: 'fas fa-address-book' },
    { to: '/tickets', label: t('navTickets'), icon: 'fas fa-headset', count: counts.data?.tickets },
    { to: '/mail', label: t('navMail'), icon: 'fas fa-envelope', count: counts.data?.mailFailed },
    { to: '/dunning', label: t('navDunning'), icon: 'fas fa-bell' },
    { to: '/templates', label: t('navTemplates'), icon: 'fas fa-file-code' },
    { to: '/portal', label: t('navPortal'), icon: 'fas fa-user-lock' },
    { to: '/settings', label: t('navSettings'), icon: 'fas fa-gears' },
  ];
  // CRM (AVI-13): only for a company that has it switched on; the import is admin-only.
  const crmNav = !isCustomer && company?.crmEnabled ? [
    { to: '/crm/pipeline', label: t('navCrmPipeline'), icon: 'fas fa-table-columns' },
    { to: '/crm/accounts', label: t('navCrmAccounts'), icon: 'fas fa-building-flag' },
    { to: '/crm/people', label: t('navCrmPeople'), icon: 'fas fa-users' },
    { to: '/crm/tasks', label: t('navCrmTasks'), icon: 'fas fa-list-check' },
    { to: '/crm/funnel', label: t('navCrmFunnel'), icon: 'fas fa-filter' },
    { to: '/crm/outreach', label: t('navCrmOutreach'), icon: 'fas fa-paper-plane' },
    ...(role === 'admin' ? [{ to: '/crm/import', label: t('navCrmImport'), icon: 'fas fa-file-import' }] : []),
  ] : [];

  const initials = (user?.name ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const roleLabel = role === 'admin' ? t('administrator') : role === 'staff' ? t('staff') : role === 'crm' ? t('crmMember') : t('customer');
  const home = isCustomer ? '/portal' : isCrm ? '/crm' : '/';

  return (
    <div className="shell">
      <header className="header">
        {!isCustomer && (
          <button type="button" className="iconbtn menubtn" aria-label={navOpen ? t('closeMenu') : t('openMenu')} aria-expanded={navOpen} onClick={() => setNavOpen((o) => !o)}>
            <i className={navOpen ? 'fas fa-xmark' : 'fas fa-bars'} aria-hidden="true" />
          </button>
        )}
        <div className="wordmark"><span className="a">Avia</span><span className="b">Console</span></div>
        {(companies.length > 1 || user?.isGlobalAdmin) && (
          <>
            <div className="pillgroup companypills" role="tablist" aria-label="Company">
              {companies.map((c) => (
                <button key={c.id} type="button" role="tab" aria-selected={c.id === company?.id} className={`pill ${c.id === company?.id ? 'active' : ''}`} onClick={() => { setCompany(c.id); navigate(home); }}>
                  <i className="fas fa-building" aria-hidden="true" style={{ fontSize: '0.7rem', opacity: 0.8 }} /> {c.shortName}
                </button>
              ))}
              {user?.isGlobalAdmin && (
                <button type="button" className={`pill sm ${location.pathname === '/companies/new' ? 'active' : ''}`} title={t('newCompany')} aria-label={t('newCompany')} onClick={() => navigate('/companies/new')}>
                  <i className="fas fa-plus" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="row companyselectwrap">
              <select className="companyselect" aria-label="Company" value={company?.id ?? ''} onChange={(e) => { setCompany(e.target.value); navigate(home); }}>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </select>
              {user?.isGlobalAdmin && (
                <button type="button" className="iconbtn sm" title={t('newCompany')} aria-label={t('newCompany')} onClick={() => navigate('/companies/new')}>
                  <i className="fas fa-plus" aria-hidden="true" />
                </button>
              )}
            </div>
          </>
        )}
        <div className="spacer" />
        <div className="row" style={{ gap: '0.6rem' }}>
          {!isCustomer && !isCrm && (
            <form onSubmit={(e) => { e.preventDefault(); const q = new FormData(e.currentTarget).get('q'); navigate(`/invoices?q=${encodeURIComponent(String(q ?? ''))}`); }} className="row headersearch" style={{ padding: '0.3rem 0.8rem', borderRadius: 'var(--radius-pill)', background: 'var(--input-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-muted)', fontSize: '0.82rem', maxWidth: 280 }}>
              <i className="fas fa-magnifying-glass" aria-hidden="true" style={{ fontSize: '0.78rem' }} />
              <input name="q" placeholder={t('search')} aria-label={t('search')} style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.82rem', outline: 'none', width: 170 }} />
            </form>
          )}
          <div className="pillgroup langpills">
            {(['en', 'nl'] as const).map((l) => <button key={l} type="button" className={`pill sm ${lang === l ? 'active' : ''}`} onClick={() => setLang(l)}>{l.toUpperCase()}</button>)}
          </div>
          <button type="button" className="iconbtn langtoggle" aria-label={t('lang')} onClick={() => setLang(lang === 'en' ? 'nl' : 'en')}>{lang.toUpperCase()}</button>
          <button type="button" className="iconbtn headertheme" title={t('theme')} aria-label={t('theme')} onClick={() => setTheme(theme === 'day' ? 'night' : 'day')}>
            <i className={theme === 'day' ? 'fas fa-moon' : 'fas fa-sun'} aria-hidden="true" />
          </button>
          <div className="row headeruser" style={{ gap: '0.55rem', paddingLeft: '0.3rem' }}>
            <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-pill)', background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>{initials}</div>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700 }}>{user?.name}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{roleLabel}</div>
            </div>
            <button type="button" className="iconbtn" title={t('signOut')} aria-label={t('signOut')} onClick={() => void logout()}><i className="fas fa-right-from-bracket" aria-hidden="true" /></button>
          </div>
        </div>
      </header>

      <div className="layout">
        {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}
        <nav className={`sidebar ${navOpen ? 'open' : ''}`} aria-label="Main">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `navitem ${isActive || (n.to !== '/' && location.pathname.startsWith(`${n.to}/`)) ? 'active' : ''}`}>
              <i className={n.icon} aria-hidden="true" />
              <span style={{ flex: 1 }}>{n.label}</span>
              {n.count ? <span className="count">{n.count}</span> : null}
            </NavLink>
          ))}
          {crmNav.length > 0 && <div className="label" style={{ padding: '0.9rem 0.8rem 0.3rem' }}>{t('navCrmSection')}</div>}
          {crmNav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `navitem ${isActive || location.pathname.startsWith(n.to) ? 'active' : ''}`}>
              <i className={n.icon} aria-hidden="true" />
              <span style={{ flex: 1 }}>{n.label}</span>
            </NavLink>
          ))}
          <div className="sidebar-footer">
            <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-pill)', background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>{initials}</div>
            <div style={{ lineHeight: 1.15, flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{roleLabel}</div>
            </div>
            <button type="button" className="iconbtn" title={t('theme')} aria-label={t('theme')} onClick={() => setTheme(theme === 'day' ? 'night' : 'day')}>
              <i className={theme === 'day' ? 'fas fa-moon' : 'fas fa-sun'} aria-hidden="true" />
            </button>
            <button type="button" className="iconbtn" title={t('signOut')} aria-label={t('signOut')} onClick={() => void logout()}><i className="fas fa-right-from-bracket" aria-hidden="true" /></button>
          </div>
        </nav>
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
